import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { GALLERIES } from '../utils/galleries';
import { useUi } from '../hooks/useUi';
import useScrollLock from '../hooks/useScrollLock';

export const SearchIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.1" aria-hidden="true">
    <circle cx="7.5" cy="7.5" r="6" />
    <line x1="12" y1="12" x2="16.5" y2="16.5" />
  </svg>
);

const PRIMARY = [
  { to: '/', label: 'Home', end: true },
  { to: '/work', label: 'Work' },
  { to: '/travel', label: 'Travel' },
  { to: '/about', label: 'About' },
  { to: '/faq', label: 'FAQ' },
];

/**
 * Fixed top bar (menu · wordmark · search) and the slide-in menu drawer.
 *   home   — light text over the hero, turns solid past it
 *   detail — gallery pages: overlays the hero on mobile, hidden on desktop
 *            (the gallery rail carries its own Menu button)
 *   solid  — dark text, background once scrolled
 */
const Nav = ({ variant }) => {
  const ui = useUi();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const drawerRef = useRef(null);
  const triggerRef = useRef(null);

  useScrollLock(ui.menuOpen);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const threshold = variant === 'solid' ? 8 : window.innerHeight * 0.82;
      setScrolled(y > threshold);
      if (y > lastY.current + 6 && y > 320) setHidden(true);
      else if (y < lastY.current - 6 || y < 320) setHidden(false);
      lastY.current = y;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);

  const { menuOpen, closeMenu } = ui;

  useEffect(() => {
    closeMenu();
  }, [pathname, closeMenu]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    drawerRef.current?.querySelector('a, button')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') closeMenu();
    };
    window.addEventListener('keydown', onKey);
    const trigger = triggerRef.current;
    return () => {
      window.removeEventListener('keydown', onKey);
      trigger?.focus({ preventScroll: true });
    };
  }, [menuOpen, closeMenu]);

  const galleries = GALLERIES.filter((g) => g.kind === 'gallery');
  const trips = GALLERIES.filter((g) => g.kind === 'trip');
  const classes = [
    'nav',
    `nav--${variant}`,
    scrolled && 'is-scrolled',
    hidden && !ui.menuOpen && 'is-hidden',
    ui.menuOpen && 'is-menu',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <header className={classes}>
        <div className="nav__inner">
          <button
            ref={triggerRef}
            type="button"
            className="nav__menu"
            aria-expanded={ui.menuOpen}
            aria-controls="site-drawer"
            aria-label={ui.menuOpen ? 'Close menu' : 'Open menu'}
            onClick={ui.menuOpen ? ui.closeMenu : ui.openMenu}
          >
            <span className="burger" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
          <Link to="/" className="nav__wordmark">
            Jonny Coe
          </Link>
          <button type="button" className="nav__search" aria-label="Search" onClick={ui.openSearch}>
            <SearchIcon />
          </button>
        </div>
      </header>

      <div className={`drawerBackdrop${ui.menuOpen ? ' is-open' : ''}`} onClick={ui.closeMenu} aria-hidden="true" />
      <aside
        id="site-drawer"
        ref={drawerRef}
        className={`drawer drawer--${variant}${ui.menuOpen ? ' is-open' : ''}`}
        aria-label="Menu"
        aria-hidden={!ui.menuOpen}
        inert={ui.menuOpen ? undefined : ''}
      >
        <button type="button" className="drawer__close" aria-label="Close menu" onClick={ui.closeMenu}>
          <span />
        </button>
        <span className="label drawer__label">Menu</span>
        <nav className="drawer__nav" aria-label="Primary">
          {PRIMARY.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `drawer__link${isActive ? ' is-active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}
          <button type="button" className="drawer__link" onClick={ui.openSearch}>
            Search
          </button>
        </nav>

        <div className="drawer__groups">
          {[
            ['Galleries', galleries],
            ['Trips', trips],
          ].map(([label, items]) => (
            <div key={label} className="drawer__group">
              <span className="label">{label}</span>
              <ul>
                {items.map((g) => (
                  <li key={g.slug}>
                    <NavLink
                      to={g.path}
                      className={({ isActive }) => `drawer__sublink${isActive ? ' is-active' : ''}`}
                    >
                      {g.title}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="drawer__foot">
          <span>Adventure, travel, and lifestyle photography.</span>
          <span className="drawer__meta">&copy; {new Date().getFullYear()} Jonny Coe</span>
        </div>
      </aside>
    </>
  );
};

export default Nav;
